import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
    Sparkles, Brain, Search, MessageSquare, Zap, ExternalLink,
    Activity, Terminal, HelpCircle, Loader2, CheckCircle2, AlertCircle, Clock
} from 'lucide-react';
import FadeIn from '../../components/FadeIn';
import { testAIConnection, callAI } from '../../utils/aiService';

const AIIntelligenceHub = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [connectedSystems, setConnectedSystems] = useState([]);
    const [researchNote, setResearchNote] = useState('');

    const [testingId, setTestingId] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [isResearching, setIsResearching] = useState(null);
    const [autoResearchResults, setAutoResearchResults] = useState({});

    useEffect(() => {
        const checkConnections = async () => {
            try {
                const docRef = doc(db, 'admin_settings', 'ai_credentials');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const systems = [
                        { id: 'openai', name: 'ChatGPT', connected: !!data.openai_key, color: '#10a37f', url: 'https://chat.openai.com', credentials: { openai_key: data.openai_key } },
                        { id: 'gemini', name: 'Google Gemini', connected: !!data.gemini_key, color: '#1a73e8', url: 'https://gemini.google.com', credentials: { gemini_key: data.gemini_key } },
                        { id: 'vertex', name: 'Vertex AI', connected: !!(data.vertex_credentials_json && data.vertex_project_id), color: '#4285f4', url: 'https://cloud.google.com/vertex-ai', credentials: { vertex_project_id: data.vertex_project_id, vertex_location: data.vertex_location, vertex_credentials_json: data.vertex_credentials_json } },
                        { id: 'perplexity', name: 'Perplexity', connected: !!data.perplexity_key, color: '#20b2aa', url: 'https://www.perplexity.ai', credentials: { perplexity_key: data.perplexity_key } },
                        { id: 'grok', name: 'Grok', connected: !!data.grok_key, color: '#ffffff', url: 'https://grok.x.ai', credentials: { grok_key: data.grok_key } },
                        { id: 'agnes', name: 'Agnes AI', connected: !!data.agnes_url, color: '#9333ea', url: 'https://app.agnes-ai.com' },
                        { id: 'manus', name: 'Manus AI', connected: !!data.manus_url, color: '#f97316', url: 'https://manus.im' }
                    ];
                    setConnectedSystems(systems);
                }
            } catch (err) {
                console.error("Error checking AI status:", err);
            } finally {
                setLoading(false);
            }
        };

        checkConnections();
    }, []);

    const handleTestConnection = async (sys) => {
        if (!sys.credentials || !sys.connected) return;

        setTestingId(sys.id);
        const result = await testAIConnection(sys.id, sys.credentials);
        setTestResults(prev => ({ ...prev, [sys.id]: result }));
        setTestingId(null);
    };

    const getStatusLabel = (sys) => {
        const result = testResults[sys.id];
        if (testingId === sys.id) return { label: 'Testing...', color: 'text-blue-400', icon: <Loader2 size={10} className="animate-spin" /> };
        if (result) {
            if (result.status === 'success') return { label: 'Operational', color: 'text-green-500', icon: <CheckCircle2 size={10} /> };
            if (result.status === 'limit') return { label: 'Quota Reached', color: 'text-orange-500', icon: <Clock size={10} /> };
            return { label: 'Key Error', color: 'text-red-500', icon: <AlertCircle size={10} /> };
        }
        return { label: sys.connected ? 'Connected' : 'Not Linked', color: sys.connected ? 'text-blue-400' : 'text-gray-500', icon: null };
    };

    const handleAutoResearch = async (prompt, cardId) => {
        // Collect all available credentials for exhaustive fallback
        const credentials = connectedSystems.reduce((acc, sys) => {
            if (sys.credentials) {
                return { ...acc, ...sys.credentials };
            }
            return acc;
        }, {});

        if (Object.keys(credentials).length === 0) {
            alert("Please connect at least one AI provider (OpenAI, Gemini, Grok, or Perplexity) first.");
            return;
        }

        setIsResearching(cardId);
        try {
            const result = await callAI('research_brief', prompt, credentials);
            setAutoResearchResults(prev => ({ ...prev, [cardId]: result }));
        } catch (err) {
            console.error("Auto Research failed:", err);
            setAutoResearchResults(prev => ({ ...prev, [cardId]: `Research Failed: ${err.message}` }));
        } finally {
            setIsResearching(null);
        }
    };

    const handleApplyResult = (cardId) => {
        const result = autoResearchResults[cardId];
        if (!result) return;

        setResearchNote(prev => {
            const separator = prev ? "\n\n---\n\n" : "";
            return prev + separator + result;
        });

        // Clear result after applying to show success
        setAutoResearchResults(prev => {
            const next = { ...prev };
            delete next[cardId];
            return next;
        });
    };

    const handleQuickDraft = async (prompt, cardId) => {
        // Collect all available credentials for exhaustive fallback
        const credentials = connectedSystems.reduce((acc, sys) => {
            if (sys.credentials) {
                return { ...acc, ...sys.credentials };
            }
            return acc;
        }, {});

        if (Object.keys(credentials).length === 0) {
            alert("Please connect at least one AI provider first.");
            return;
        }

        setIsResearching(cardId);
        try {
            const result = await callAI('research_brief', prompt, credentials);

            // Create the package immediately
            const packagedItem = {
                title: `Automated Brief (${new Date().toLocaleDateString()})`,
                summary: result.substring(0, 200) + '...',
                aiResearch: result,
                url: 'AI Intelligence Hub (Auto)',
                source: 'Automated Research',
                topics: ['Market Intelligence'],
                useAI: true
            };

            // Navigate immediately
            navigate('/admin/blog/new', { state: { newsItem: packagedItem } });
        } catch (err) {
            alert(`Drafting failed: ${err.message}`);
        } finally {
            setIsResearching(null);
        }
    };

    const handlePreparePackage = () => {
        if (!researchNote.trim()) {
            alert("Please paste some research insights first.");
            return;
        }

        // Create a "Manual Signal" package for the Blog Editor
        const packagedItem = {
            title: `Custom Intelligence Briefing (${new Date().toLocaleDateString()})`,
            summary: researchNote.substring(0, 200) + '...',
            aiResearch: researchNote,
            url: 'AI Intelligence Hub',
            source: 'Manual Research',
            topics: ['Market Intelligence', 'AI Analysis'],
            useAI: true // Manual package implies user wants high-quality fallback/processing
        };

        // Navigate to Blog Editor with this manual context
        navigate('/admin/blog/new', { state: { newsItem: packagedItem } });
    };

    const researchContext = "Act as a senior BPO consultant and market researcher. Analysis should focus on offshore staffing, IT-BPM growth, and digital transformation in the Philippines and G7 markets. Provide data-backed insights suitable for executive readers.";

    if (loading) return <div className="p-10 text-center text-gray-400">Synchronizing AI Intelligence...</div>;

    return (
        <div className="max-w-6xl mx-auto py-8 px-4">
            <FadeIn>
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                            <Sparkles className="text-[#A2CB52]" /> AI Intelligence Hub
                        </h1>
                        <p className="text-gray-400">Central nervous system for high-authority content research and generation.</p>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-[#A2CB52]/10 rounded-full border border-[#A2CB52]/20">
                        <Activity size={14} className="text-[#A2CB52] animate-pulse" />
                        <span className="text-[10px] font-bold text-[#A2CB52] uppercase tracking-widest">Global Intelligence Active</span>
                    </div>
                </div>

                {/* System Status Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
                    {connectedSystems.map(sys => {
                        const status = getStatusLabel(sys);
                        return (
                            <div key={sys.id} className="p-4 rounded-2xl border bg-[#0f1520] transition-all hover:scale-[1.05] group" style={{ borderColor: sys.connected ? `${sys.color}40` : 'var(--border)' }}>
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-8 h-8 rounded flex items-center justify-center font-bold text-xs" style={{ background: `${sys.color}20`, color: sys.color }}>
                                        {sys.name.charAt(0)}
                                    </div>
                                    <div className={`w-2 h-2 rounded-full ${sys.connected ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-gray-600'}`}></div>
                                </div>
                                <h3 className="text-white text-xs font-bold mb-1">{sys.name}</h3>
                                <div className="flex items-center gap-1.5 mb-1">
                                    {status.icon}
                                    <p className={`text-[9px] uppercase tracking-tighter font-bold ${status.color}`}>
                                        {status.label}
                                    </p>
                                </div>

                                {testResults[sys.id]?.message && (
                                    <p className="text-[8px] text-gray-400 italic mb-2 line-clamp-2 leading-tight h-4 overflow-hidden" title={testResults[sys.id].message}>
                                        {testResults[sys.id].message}
                                    </p>
                                )}

                                <div className={testResults[sys.id]?.message ? "" : "mt-2"}>
                                    {sys.credentials && sys.connected && (
                                        <button
                                            onClick={() => handleTestConnection(sys)}
                                            disabled={testingId === sys.id}
                                            className="w-full py-1.5 rounded-lg bg-white/5 border border-white/10 text-[9px] font-bold text-gray-400 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50"
                                        >
                                            {testingId === sys.id ? 'Testing...' : 'Test Status'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* AI Troubleshooting & Resolution Guide */}
                <div className="mb-12 p-6 rounded-3xl border border-blue-500/20 bg-blue-500/5">
                    <h2 className="text-sm font-bold text-blue-400 mb-4 flex items-center gap-2 uppercase tracking-widest">
                        <AlertCircle size={16} /> AI Service Resolution Guide
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                <span className="w-4 h-4 rounded-full bg-blue-500 text-[10px] flex items-center justify-center">1</span>
                                OpenAI Quota
                            </h4>
                            <p className="text-[10px] text-gray-400 leading-normal italic">Error: "exceeded your current quota"</p>
                            <p className="text-[11px] text-gray-300 leading-relaxed">
                                This means your <strong>OpenAI Platform billing account</strong> needs credits. Note: This is separate from ChatGPT Plus. Login to <a href="https://platform.openai.com/billing" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">OpenAI Billing</a> and add $5-10 to reactivate.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                <span className="w-4 h-4 rounded-full bg-blue-500 text-[10px] flex items-center justify-center">2</span>
                                Gemini Key Error
                            </h4>
                            <p className="text-[10px] text-gray-400 leading-normal italic">Error: "models/gemini-pro not found"</p>
                            <p className="text-[11px] text-gray-300 leading-relaxed">
                                Ensure your key is from <a href="https://aistudio.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google AI Studio</a>. Make sure you have enabled the <strong>Generative Language API</strong> in the project associated with your key.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                <span className="w-4 h-4 rounded-full bg-blue-500 text-[10px] flex items-center justify-center">3</span>
                                CORS / Proxy Issues
                            </h4>
                            <p className="text-[10px] text-gray-400 leading-normal italic">Error: "Failed to fetch" (Perplexity/Grok)</p>
                            <p className="text-[11px] text-gray-300 leading-relaxed">
                                Most AI providers (Perplexity, Grok) block direct browser requests for security. If you see CORS errors, ensure your API keys have <strong>"Allow All Origins"</strong> or are used via a server-side proxy. For Grok, verify your account is active at <a href="https://console.x.ai/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">console.x.ai</a>.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Research Prompter */}
                    <div className="lg:col-span-2 space-y-8">
                        <section className="p-8 rounded-3xl border bg-[#0f1520] border-[#1e2d4a]">
                            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                                <Search className="text-[#A2CB52]" size={20} /> Deep Research Prompter
                            </h2>
                            <p className="text-sm text-gray-400 mb-6 leading-relaxed">Use these quick prompts to gather "Updated Data" and "Market News". This context ensures your articles are trusted by Google's E-E-A-T and AI systems.</p>

                            <div className="bg-[#090e18] p-5 rounded-xl border border-[#1e2d4a] mb-8 font-mono text-xs text-blue-400 leading-normal relative">
                                <div className="absolute top-4 right-4 text-[10px] text-gray-600 font-bold uppercase tracking-widest">Global Context Filter</div>
                                {researchContext}
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                {[
                                    { id: 'stats', label: 'Verify Latest Stats', prompt: 'Find the latest 2026 data points for BPO growth in the Philippines. ' + researchContext, icon: <Zap size={18} className="text-teal-500" />, color: 'teal', link: 'https://www.perplexity.ai/search?q=' },
                                    { id: 'analysis', label: 'Generate Analysis', prompt: 'Analyze the operational impact of AI on offshore staffing for 2026. ' + researchContext, icon: <Brain size={18} className="text-green-500" />, color: 'green', link: 'https://chatgpt.com/?q=' },
                                    { id: 'manus', label: 'Live Agent Research', prompt: 'Summarize the latest 2026 BPO market trends from Manus AI. ' + researchContext, icon: <Terminal size={18} className="text-orange-500" />, color: 'orange', link: 'https://manus.im/' },
                                    { id: 'agnes', label: 'Agnes AI Brief', prompt: 'Generate a strategic brief on digital transformation in Philippine BPO. ' + researchContext, icon: <MessageSquare size={18} className="text-purple-500" />, color: 'purple', link: 'https://app.agnes-ai.com/' }
                                ].map(card => (
                                    <div key={card.id} className="space-y-2">
                                        <div className="p-5 rounded-2xl border border-[#1e2d4a] bg-[#141c2e] flex flex-col gap-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    {card.icon}
                                                    <span className="text-sm text-white font-medium">{card.label}</span>
                                                </div>
                                                <a
                                                    href={card.link + encodeURIComponent(card.prompt)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-all"
                                                    title="Open External Tool"
                                                >
                                                    <ExternalLink size={14} />
                                                </a>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 mt-auto">
                                                <button
                                                    onClick={() => handleAutoResearch(card.prompt, card.id)}
                                                    disabled={isResearching === card.id}
                                                    className={`py-2 rounded-xl border border-${card.color}-500/20 bg-${card.color}-500/5 text-${card.color}-500 text-[9px] font-bold uppercase tracking-widest hover:bg-${card.color}-500/10 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5`}
                                                >
                                                    {isResearching === card.id ? (
                                                        <Loader2 size={10} className="animate-spin" />
                                                    ) : <Brain size={10} />}
                                                    In-Hub
                                                </button>
                                                <button
                                                    onClick={() => handleQuickDraft(card.prompt, card.id)}
                                                    disabled={isResearching === card.id}
                                                    className={`py-2 rounded-xl bg-${card.color}-500 text-white text-[9px] font-bold uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5`}
                                                >
                                                    {isResearching === card.id ? (
                                                        <Loader2 size={10} className="animate-spin" />
                                                    ) : <Zap size={10} />}
                                                    Draft
                                                </button>
                                            </div>
                                        </div>

                                        {autoResearchResults[card.id] && (
                                            <div className="p-4 rounded-2xl border border-[#A2CB52]/20 bg-[#A2CB52]/5 animate-in fade-in slide-in-from-top-2 duration-300">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-[10px] font-bold text-[#A2CB52] uppercase tracking-widest">Research Result</h4>
                                                    <button
                                                        onClick={() => handleApplyResult(card.id)}
                                                        className="px-2 py-1 rounded-lg bg-[#A2CB52] text-[#20222D] text-[9px] font-bold hover:scale-105 active:scale-95 transition-all"
                                                    >
                                                        Apply to Package
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-gray-300 leading-relaxed font-mono">
                                                    {autoResearchResults[card.id]}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* Quick Insight Collector */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-8">
                            <section className="p-8 rounded-3xl border bg-[#0f1520] border-[#1e2d4a] h-full">
                                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                                    <MessageSquare className="text-[#A2CB52]" size={20} /> Research Insights
                                </h2>
                                <p className="text-xs text-gray-400 mb-6 leading-relaxed">Paste data points and news snippets found using the tools above. These will be used to enhance your next article.</p>

                                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex gap-3">
                                    <HelpCircle className="text-blue-400 shrink-0" size={16} />
                                    <div>
                                        <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-1">How to use</h4>
                                        <p className="text-[10px] text-gray-400 leading-normal">
                                            1. Click "Verify Stats" or "Generate Analysis" to open AI tools.<br />
                                            2. Copy key data points/analysis from those tabs.<br />
                                            3. Paste them here to "Package" them.<br />
                                            4. Clicking the button below will take you to the drafting wizard with this research pre-loaded.
                                        </p>
                                    </div>
                                </div>

                                <textarea
                                    rows="12"
                                    className="w-full bg-[#090e18] border border-[#1e2d4a] rounded-2xl p-4 text-xs text-gray-300 outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all mb-4"
                                    placeholder="e.g. 'According to Perplexity, the Philippines BPO market is projected to reach $38B by late 2026...'"
                                    value={researchNote}
                                    onChange={(e) => setResearchNote(e.target.value)}
                                ></textarea>

                                <button
                                    onClick={handlePreparePackage}
                                    className="w-full py-4 bg-[#A2CB52] text-[#20222D] rounded-2xl font-bold hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    <Zap size={18} /> Prepare Content Package
                                </button>
                            </section>
                        </div>
                    </div>
                </div>
            </FadeIn>
        </div>
    );
};

export default AIIntelligenceHub;
